import { createAction, handleActions } from 'redux-actions';
import { createSelector } from 'reselect';
import _some from 'lodash/some';
import _includes from 'lodash/includes';
import _sortby from 'lodash/sortBy';
import _reduce from 'lodash/reduce';
import { knownFacets } from '../lib/constants';
import update from 'immutability-helper';

const actionsPrefix = 'app-job-search/filters';
const DEFAULT_AGE_RANGE = [0, 19];

export const INITIAL_STATE = {
  selectedFacets: [],
  facets: [],
  someFacetsSelected: false,
  keywords: '',
  sort: 'date',
  viewtype: 'list',
  mapView: false,
  metadata: null,
  toggled: false,
  maxdistance: '',
  point: '',
  bbox: '',
  displayLocation: '',
  toggles: '',
  ageRange: null,
  ageRangeFromQS: null,
  showMoreVisible: false,
  selectedMoreFilter: '',
  locationBreadcrumb: [],
  experiments: []
};

// setters (actions)
export const setSelectedFacets = createAction(`${actionsPrefix}/SET_SELECTED_FACETS`);
export const clearSelectedFacets = createAction(`${actionsPrefix}/CLEAR_SELECTED_FACETS`);
export const clearFilters = createAction(`${actionsPrefix}/CLEAR_FILTERS`);
export const setFacets = createAction(`${actionsPrefix}/SET_FACETS`);
export const setLoadingFacet = createAction(`${actionsPrefix}/SET_LOADING_FACET`);
export const setKeywords = createAction(`${actionsPrefix}/SET_KEYWORDS`);
export const setMetadata = createAction(`${actionsPrefix}/SET_METADATA`);
export const setFromQueryString = createAction(`${actionsPrefix}/SET_FROM_QUERY_STRING`);
export const setToggled = createAction(`${actionsPrefix}/SET_TOGGLED`);
export const setSort = createAction(`${actionsPrefix}/SET_SORT`);
export const setMaxDistance = createAction(`${actionsPrefix}/SET_MAX_DISTANCE`);
export const setViewType = createAction(`${actionsPrefix}/SET_VIEW_TYPE`);
export const setMapView = createAction(`${actionsPrefix}/SET_MAP_VIEW`);
export const setPoint = createAction(`${actionsPrefix}/SET_POINT`);
export const setBbox = createAction(`${actionsPrefix}/SET_BBOX`);
export const setDisplayLocation = createAction(`${actionsPrefix}/SET_DISPLAY_LOCATION`);
export const setAgeRange = createAction(`${actionsPrefix}/SET_AGE_RANGE`);
export const toggleShowMoreVisible = createAction(`${actionsPrefix}/TOGGLE_SHOWMORE_VISIBLE`);
export const setSelectedMoreFilter = createAction(`${actionsPrefix}/SET_SELECTED_MORE_FILTER`);
export const setExperiments = createAction(`${actionsPrefix}/SET_EXPERIMENTS`);
export const setCanAddToProfile = createAction(`${actionsPrefix}/CAN_ADD_TO_PROFILE`);
export const setLocationBreadcrumb = createAction(`${actionsPrefix}/SET_LOCATION_BREADCRUMB`);
export const addToProfile = createAction(`${actionsPrefix}/ADD_TO_PROFILE`);

// immutable helpers
const updatedItem = (itm) => {
  const newItem = update(itm, {
    loading: { $set: true }
  });
  return newItem;
};

const setUpdateFacetGroup = (groups, field, id) => { // I know it looks weird, but we have weird nesting :D
  const newGroups = groups.map(group => {
    if (groups[field]) {
      const newGroup = update(groups[field], {
        items: {
          $apply: items => {
            return items.map(itm => {
              if (itm.id === id) {
                return updatedItem(itm);
              }
              const newItem = itm.items ? update(itm, {
                items: {
                  $apply: subItems => {
                    return subItems.map(subItm => {
                      if (subItm.id === id) {
                        return updatedItem(subItm);
                      }
                      return subItm;
                    });
                  }
                }
              }) : itm;
              return newItem;
            });
          }
        }
      });
      return newGroup;
    }
    return group;
  });
  return newGroups;
};

const orderByHierarchy = (facet) => {
  const newFacets = [];
  facet.buckets.forEach( o => {
    const level = (o.parent) ? 2 : 1;
    const value = o.val.split(':').pop();
    if (level === 1) {
      newFacets.push({
        facetItemPath: o.val,
        value,
        label: value,
        level: 1,
        isActive: o.isActive,
        count: o.count
      });
      facet.buckets.forEach( level2Obj => {
        const splittedValue = level2Obj.val.split(':');
        const level2level  = splittedValue.length - 1;
        const level2value = level2Obj.val.split(':').pop();
        if (level2level === 2 && splittedValue[level2level - 1] === value && o.isActive ) {
          newFacets.push({
            facetItemPath: level2Obj.val,
            value: level2value,
            label: level2value,
            level: 2,
            isActive: level2Obj.isActive,
            count: level2Obj.count
          });
        }
      });
    }
  });
  return newFacets;
};

const facetsMapping = (facets) => {
  Object.keys(facets).forEach( key => {
    if (facets[key].buckets) {
      let newFacets = [];
      if (key === 'ages') {
        newFacets = facets[key].buckets.map( o => o.val.toString());
      } else {
        newFacets = orderByHierarchy(facets[key]);
      }
      facets[key] = ( key === 'locations' ) ? _sortby(newFacets, ['count']).reverse() : newFacets;
    }
  });
  return facets;
};

const getMappedFrom = selected => {
  if (!selected.mappedFrom) return false;
  if (!selected.val.toLowerCase || !selected.mappedFrom.toLowerCase) return false;
  if (selected.mappedFrom.toLowerCase() === selected.val.toLowerCase()) return selected.mappedFrom;
  if (selected.mappedFrom.toLowerCase() !== selected.val.toLowerCase()) return `${selected.mappedFrom}:${selected.val.split(':').pop()}`;
};

export default handleActions({
  [setSelectedFacets]: (state, { payload: { selectedFacets, facets = [] } }) => {
    let newSelectedFacets = selectedFacets.filter( selectedFacet => selectedFacet.name !== 'organisationId' );
    Object.keys(facets).forEach( (key) => {
      const facet = facets[key];
      if ( !facet.selected || !facet.selected.length ) return;
      newSelectedFacets = newSelectedFacets.filter( selectedFacet => selectedFacet.name !== key );
      newSelectedFacets.push({
        name: key,
        value: facet.selected.map( o => o.val ),
        mappedFrom: facet.selected.map(o => ({val: o.val.split(':').pop(), mappedFrom: getMappedFrom(o)}))
      });
    });
    return {
      ...state,
      selectedFacets: newSelectedFacets,
      someFacetsSelected: _some(newSelectedFacets, ({ name }) => _includes(knownFacets, name))
    };
  },
  [clearSelectedFacets]: (state) => ({...state, selectedFacets: [], someFacetsSelected: false}),
  [clearFilters]: (state) => ({...state, selectedFacets: [], someFacetsSelected: false, keywords: '', sort: 'date', maxdistance: '', point: '', displayLocation: '', showMoreVisible: false, selectedMoreFilter: '', locationBreadcrumb: []}),
  [setFacets]: (state, { payload: facetsRaw }) => ({ ...state, facets: facetsMapping(facetsRaw) }),
  [setLoadingFacet]: (state, { payload: { field, id } }) => {
    const newState = update(state, {
      facets: {
        $apply: (groups) => setUpdateFacetGroup(groups, field, id)
      }
    });
    return { ...newState };
  },
  [setMetadata]: (state, { payload: metadata }) => ({ ...state, metadata }),
  [setKeywords]: (state, { payload: keywords }) => ({ ...state, keywords }),
  [setFromQueryString]: (state, { payload: query }) => {
    let displayLocation = '';
    if (query.displayLocation) displayLocation = query.displayLocation;
    if (Array.isArray(query.locations)) query.locations = query.locations[0]; // fix multipe location
    if (!displayLocation && query.locations) displayLocation = query.locations.split(':').pop();
    return {
      ...state,
      ...query,
      maxdistance: query.maxdistance,
      mapView: query.viewtype && query.viewtype === 'map',
      ageRange: query.ageRange ? query.ageRange : null,
      ageRangeFromQS: query.ageRange,
      keywords: query.keywords ? query.keywords : '',
      displayLocation,
      restrict: query.restrict,
      addToProfile: query.addToProfile,
    };
  },
  [setExperiments]: (state, { payload: experiments }) => ({ ...state, experiments }),
  [setCanAddToProfile]: (state, { payload: canAddToProfile }) => ({ ...state, canAddToProfile }),
  [setToggled]: (state, { payload }) => ({ ...state, toggled: typeof payload !== 'undefined' ? payload : !state.toggled }),
  [setSort]: (state, { payload: sort }) => ({ ...state, sort }),
  [setMaxDistance]: (state, { payload: maxdistance }) => ({ ...state, maxdistance: ~~maxdistance }),
  [setPoint]: (state, { payload: point }) => ({ ...state, point }),
  [setBbox]: (state, { payload: bbox }) => ({ ...state, bbox }),
  [setViewType]: (state, { payload: viewtype }) => ({ ...state, viewtype, mapView: viewtype === 'map' }),
  [setMapView]: (state, { payload: mapView }) => ({ ...state, mapView }),
  [setDisplayLocation]: (state, { payload: displayLocation }) => ({ ...state, displayLocation }),
  [setAgeRange]: (state, { payload: ageRange }) => ({ ...state, ageRange }),
  [toggleShowMoreVisible]: (state, { payload: value }) => ({ ...state, showMoreVisible: ( typeof value === 'boolean' ) ? value : !state.showMoreVisible }),
  [setSelectedMoreFilter]: (state, { payload: value }) => ({ ...state, selectedMoreFilter: value }),
  [setLocationBreadcrumb]: (state, { payload: value }) => ({ ...state, locationBreadcrumb: value })
}, INITIAL_STATE);

// getters (selectors)
export const get = state => state.filters;
export const getSelectedFacets = createSelector(get, ({ selectedFacets }) => selectedFacets);
export const getSomeSelectedFacets = createSelector(get, ({ someFacetsSelected }) => someFacetsSelected);
export const getFacets = createSelector(get, ({ facets }) => facets);
export const getRestrict = createSelector(get, ({ restrict }) => restrict);
export const getFacetGroups = createSelector(getFacets, ({ facetGroups }) => facetGroups);
export const getKeywords = createSelector(get, ({ keywords }) => keywords);
export const getMetadata = createSelector(get, ({ metadata }) => metadata);
export const getCanAddToProfile = createSelector(get, ({ canAddToProfile }) => canAddToProfile);
export const getNoAds = createSelector(get, ({ noAds }) => noAds);
export const getViewType = createSelector(get, ({ viewtype }) => viewtype);
export const getMapView = createSelector(get, ({ mapView }) => mapView);
export const getToggled = createSelector(get, ({ toggled }) => toggled);
export const getMaxDistance = createSelector(get, ({ maxdistance }) => maxdistance);
export const getPoint = createSelector(get, ({ point }) => point);
export const getBbox = createSelector(get, ({ bbox }) => bbox);
export const getIntervals = createSelector(getFacets, ({ distance }) => distance && distance.maxDistance && distance.maxDistance.intervals || []);
export const getSortMetadata = createSelector(getMetadata, ({ sort }) => sort);
export const getSort = createSelector(get, ({ sort }) => sort);
export const getDisplayLocation = createSelector(get, ({ displayLocation }) => displayLocation);
export const getToggles = createSelector(get, ({ toggles }) => toggles);
export const getAgeRange = createSelector(get, ({ ageRange }) => ageRange ? decodeURIComponent(ageRange).split(',').map(age => ~~age) : DEFAULT_AGE_RANGE);
export const getAgeRangeFromQS = createSelector(get, ({ ageRangeFromQS }) => ageRangeFromQS);
export const getAgeRangeLabel = createSelector(getAgeRange, ageRange =>
  ageRange.join(',') !== DEFAULT_AGE_RANGE.join(',') ? ageRange.map(age => age === 19 ? '18+' : '' + age).join(' - ') : 'All'
);
export const getTotalJobs = createSelector(get, ({ totalJobs }) => totalJobs);
export const getRole = createSelector(get, ({ role }) => role);
export const getShowMoreVisible = createSelector(get, ({ showMoreVisible }) => showMoreVisible);
export const getSelectedMoreFilter = createSelector(get, ({ selectedMoreFilter }) => selectedMoreFilter);
export const getLocationBreadcrumb = createSelector(get, ({ locationBreadcrumb }) => locationBreadcrumb);
export const getLocations = createSelector(get, ({ locations }) => locations);
export const getPrimaryColor = createSelector(get, ({ primaryColor }) => primaryColor);
export const getSecondaryColor = createSelector(get, ({ secondaryColor }) => secondaryColor);
export const getExperiments = createSelector(get, ({ experiments }) => {
  return _reduce(experiments, (obj, param) => {
    obj[param.split('=')[0]] = param.split('=')[1];
    return obj;
  }, {});
});
